import { useTranslation } from 'react-i18next';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { formatInteger } from '@/lib/language';

export interface LargeRunConfirmDialogProps {
  open: boolean;
  count: number;
  /** Restkapazitaet NACH dem Lauf, null = unbekannt (dann kein Satz dazu). */
  remainingAfter: number | null;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Abbrechen laeuft AUSSCHLIESSLICH ueber `onOpenChange` (Knopf oder Escape;
 * ein AlertDialog schliesst nicht per Klick daneben), Bestaetigen ist ein
 * normaler Knopf ohne Close-Verhalten. Radix'
 * `AlertDialogCancel`/`AlertDialogAction` rufen nach ihrem `onClick` selbst
 * `onOpenChange(false)`; mit Handlern an beiden Stellen liefe `onCancel`
 * doppelt bzw. nach `onConfirm`.
 */
export function LargeRunConfirmDialog({ open, count, remainingAfter, onConfirm, onCancel }: LargeRunConfirmDialogProps) {
  const { t, i18n } = useTranslation('optimization');
  const lang = i18n.language;
  return (
    <AlertDialog open={open} onOpenChange={(next) => { if (!next) onCancel(); }}>
      <AlertDialogContent data-testid="large-run-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t('feedCheck.largeRun.title', { count, formatted: formatInteger(count, lang) })}
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-2 text-sm text-muted-foreground">
              <p>{t('feedCheck.largeRun.body')}</p>
              {remainingAfter !== null && (
                <p>
                  {t('feedCheck.largeRun.capacityAfter', {
                    count: remainingAfter,
                    formatted: formatInteger(remainingAfter, lang),
                  })}
                </p>
              )}
              <p>{t('feedCheck.largeRun.reviewNote', { formatted: formatInteger(count, lang) })}</p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="large-run-cancel">
            {t('feedCheck.largeRun.cancelRun')}
          </AlertDialogCancel>
          <Button data-testid="large-run-confirm" onClick={onConfirm}>
            {t('feedCheck.largeRun.confirmRun')}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
